const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const hexToRgb = hex => {
  const xHex = hex.replace('#', '').trim();
  if (xHex.length !== 6) return null;
  const xNum = Number.parseInt(xHex, 16);
  if (Number.isNaN(xNum)) return null;

  return {
    r: (xNum >> 16) & 255,
    g: (xNum >> 8) & 255,
    b: xNum & 255
  };
};

export const rgbToHex = ({ r, g, b }) => {
  const toHex = value => clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const rgbToHsl = ({ r, g, b }) => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === rn) h = ((gn - bn) / delta) % 6;
    else if (max === gn) h = (bn - rn) / delta + 2;
    else h = (rn - gn) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  return {
    h,
    s: s * 100,
    l: l * 100
  };
};

export const hslToRgb = ({ h, s, l }) => {
  const sn = clamp(s, 0, 100) / 100;
  const ln = clamp(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hh = h / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));

  let r1 = 0;
  let g1 = 0;
  let b1 = 0;

  if (hh >= 0 && hh < 1) {
    r1 = c;
    g1 = x;
  } else if (hh >= 1 && hh < 2) {
    r1 = x;
    g1 = c;
  } else if (hh >= 2 && hh < 3) {
    g1 = c;
    b1 = x;
  } else if (hh >= 3 && hh < 4) {
    g1 = x;
    b1 = c;
  } else if (hh >= 4 && hh < 5) {
    r1 = x;
    b1 = c;
  } else {
    r1 = c;
    b1 = x;
  }

  const m = ln - c / 2;

  return {
    r: (r1 + m) * 255,
    g: (g1 + m) * 255,
    b: (b1 + m) * 255
  };
};

export const adjustLightness = (hex, delta) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const hsl = rgbToHsl(rgb);
  return rgbToHex(hslToRgb({ ...hsl, l: clamp(hsl.l + delta, 0, 100) }));
};

const linearize = channel => {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

export const relativeLuminance = hex => {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const r = linearize(rgb.r);
  const g = linearize(rgb.g);
  const b = linearize(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrastRatio = (hexA, hexB) => {
  const l1 = relativeLuminance(hexA);
  const l2 = relativeLuminance(hexB);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

export const ensureContrast = ({ foreground, background, minRatio, darkenFirst = true }) => {
  let current = foreground;
  if (contrastRatio(current, background) >= minRatio) return current;

  const step = 2;
  for (let i = 0; i < 30; i += 1) {
    current = adjustLightness(current, darkenFirst ? -step : step);
    if (contrastRatio(current, background) >= minRatio) return current;
  }

  return darkenFirst ? '#101323' : '#ffffff';
};

export const withAlpha = (hex, alpha = 1) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)}, ${alpha})`;
};

export const clampHsl = ({ h, s, l }) => ({
  h: (h % 360 + 360) % 360,
  s: clamp(s, 0, 100),
  l: clamp(l, 0, 100)
});
